<script lang="ts">
    import { SelectSlider } from "@/Common";
    import type { ProgramFocusTypes, SpecificityTypes } from "@/Common/constants";
    import { getLibxert } from "@/xert-wasm";
    import { cn } from "@/lib/utils";
    import { fade } from "svelte/transition";
    import SpecificityPill from "./SpecificityPill.svelte";

    /**
     * A Goal's focus: a slider of the focus types from the longest duration to the shortest, with the specificity beside it
     * and the focus power below, under the Threshold, VO2max and Anaerobic boxes of the account setup wizard. The boxes only
     * show the focus: they are sized by how much of each it is, overlapping like a cover flow, the bigger in front and the
     * smaller turned away towards the middle.
     *
     * The sizes follow Threshold Power's share of the focus power (its elasticity in the signature's power at the focus
     * duration), which rises with the duration: 2 minute power (Anaerobic Focus) is 0, 6 minute (VO2max Focus) is 1/2 and
     * 20 minute (Threshold Focus) is 1, in a straight line by the share between them. Threshold goes from small to big over
     * that, Anaerobic from big to small, and VO2max is big at 1/2 and small at either end.
     */
    export let athleteTypes: ProgramFocusTypes[]
    // Each focus type's duration, in seconds
    export let durations: Record<string, number>
    export let targetAthlete: ProgramFocusTypes
    export let specificity: SpecificityTypes
    // Today's signature: Threshold Power and Peak Power in watts, High Intensity Energy in joules
    export let signature: { ftp: number, hie: number, pp: number } | null
    export let currentFocusPower: number
    export let focusPower: number
    export let disabled = false
    export let onFocusInput: () => void = () => {}
    export let onSpecificityInput: () => void = () => {}
    let className = ""
    export {className as class}

    // Longest first, as the boxes go from Threshold to Anaerobic. Made once: the form passes the same types each time it
    // updates, and the slider would go back to the focus it had while being dragged.
    const types = [...athleteTypes].sort((a, b) => durations[b] - durations[a])
    let focusIndex = 0
    $: focusIndex = types.indexOf(targetAthlete)
    // From the input itself, as its event comes before the binding has the new value
    function onSlide(e: Event) {
        const index = Number((e.target as HTMLInputElement).value)
        if (types[index] === targetAthlete) return
        targetAthlete = types[index]
        onFocusInput()
    }

    const boxes = [
        { key: "threshold", label: "Threshold", icon: "fa-mountain",
            description: "Power you can hold for long, steady efforts and climbs." },
        { key: "vo2max", label: "VO2Max", icon: "fa-bicycle",
            description: "Your aerobic ceiling, for hard efforts of a few minutes." },
        { key: "anaerobic", label: "Anaerobic", icon: "fa-bolt",
            description: "Your top end, for short, all-out efforts and attacks." },
    ]
    // The anchors' durations, in seconds
    const ANAEROBIC = 120, VO2MAX = 360, THRESHOLD = 1200
    // A box's size, small and big, as a share of its full width and height. Neighbouring boxes overlap by OVERLAP of the
    // narrower one's width, so that a small box's label shows, and a box turns away by up to ANGLE degrees as it gets smaller.
    const SMALL = 0.4, BIG = 1, OVERLAP = 0.2, ANGLE = 35
    // The narrowest a box gets, in pixels, so that its label fits on a phone
    const MIN_WIDTH = 72
    // The full width fits the three in the row at their widest (a box big and two halfway, at 6 minutes), up to 230 pixels.
    // The boxes change their width and height, not their scale, so their text stays the same size.
    let rowWidth = 0
    $: width = Math.min(230, rowWidth/(BIG + SMALL + BIG - 2*OVERLAP*(BIG + SMALL)/2 + 0.05))
    $: height = Math.max(128, 0.7*width)

    let position = 0.5
    // Found again only when the signature or the focus changes, not each time the form updates
    let positionKey = ""
    $: if (signature) {
        const key = `${signature.ftp} ${signature.hie} ${signature.pp} ${durations[targetAthlete]}`
        if (key !== positionKey) {
            positionKey = key
            updatePosition(signature, durations[targetAthlete])
        }
    }
    let positionRequest = 0
    async function updatePosition(sig: { ftp: number, hie: number, pp: number }, duration: number) {
        const request = ++positionRequest
        const libxert = await getLibxert()
        if (request !== positionRequest || !duration) return
        // Threshold Power's share of the power at a duration: its elasticity over the three's
        const share = (d: number) => {
            const power = (ftp: number, hie: number, pp: number) => libxert.power_from_duration(pp, hie, ftp, d)
            const p = power(sig.ftp, sig.hie, sig.pp), h = 1e-3
            const e = [
                (power(sig.ftp*(1 + h), sig.hie, sig.pp) - p)/h,
                (power(sig.ftp, sig.hie*(1 + h), sig.pp) - p)/h,
                (power(sig.ftp, sig.hie, sig.pp*(1 + h)) - p)/h,
            ]
            return e[0]/(e[0] + e[1] + e[2])
        }
        const [s2, s6, s20, s] = [share(ANAEROBIC), share(VO2MAX), share(THRESHOLD), share(duration)]
        const t = s <= s6 ? 0.5*(s - s2)/(s6 - s2) : 0.5 + 0.5*(s - s6)/(s20 - s6)
        position = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0.5
    }
    $: sizes = [
        SMALL + (BIG - SMALL)*position,
        BIG - (BIG - SMALL)*Math.abs(2*position - 1),
        BIG - (BIG - SMALL)*position,
    ]
    // Each box's width, and where its centre is, side by side with their overlaps, and the row centred
    $: widths = sizes.map(size => Math.max(MIN_WIDTH, width*size))
    $: centres = ((): number[] => {
        const w = widths
        const x = [0, w[0]/2 + w[1]/2 - OVERLAP*Math.min(w[0], w[1])]
        x.push(x[1] + w[1]/2 + w[2]/2 - OVERLAP*Math.min(w[1], w[2]))
        const middle = (x[0] - w[0]/2 + x[2] + w[2]/2)/2
        return x.map(v => v - middle)
    })()
    // How far a box is from small (0) to big (1), for the size of its icon and label
    $: grown = sizes.map(size => (size - SMALL)/(BIG - SMALL))
    // The box the focus is most of, in front
    $: front = sizes.indexOf(Math.max(...sizes))
    // The bigger box in front, the others behind it the further they are from it, and a smaller one at the side turned as
    // in a cover flow, its outer edge going back and its inner edge under the box in front, and shorter. Reactive, so that
    // the boxes follow the sizes.
    $: boxStyle = (i: number) => {
        const turn = i === 1 ? 0 : (i === 0 ? -1 : 1)*ANGLE*(BIG - sizes[i])/(BIG - SMALL)
        const h = height*(0.4 + 0.6*sizes[i])
        // A box behind another has its icon and label centred in the part the one in front of it leaves uncovered
        const towardFront = i < front ? i + 1 : i - 1
        const covered = i === front ? 0 : OVERLAP*Math.min(widths[i], widths[towardFront])
        const padding = i < front ? `padding-right: ${covered + 8}px;` : i > front ? `padding-left: ${covered + 8}px;` : ""
        return `width: ${widths[i]}px; height: ${h}px; top: ${(height - h)/2 + 6}px; z-index: ${10 - Math.abs(i - front)}; ${padding}`
            + ` transform: translateX(calc(-50% + ${centres[i]}px)) rotateY(${turn}deg)`
    }

</script>

<div class={cn("flex flex-col gap-2", className)}>
    <div class="relative w-full overflow-hidden" style="height: {height + 12}px; perspective: 800px" bind:clientWidth={rowWidth} aria-hidden="true">
        {#if width > 0}
            {#each boxes as box, i}
                <div class={cn("absolute left-1/2 flex flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border-2 px-2 text-center transition-all duration-300 ease-out",
                        front === i ? "border-xerteal-400 bg-xerteal-50 text-xerteal-700 shadow-lg"
                            : "border-slate-200 bg-gradient-to-b from-white to-slate-100 text-slate-500 shadow-md")}
                    style={boxStyle(i)}>
                    <i class={cn("fa-regular transition-all duration-300", box.icon)} style="font-size: {12 + 12*grown[i]}px"/>
                    <span class="font-semibold leading-tight whitespace-nowrap transition-all duration-300" style="font-size: {11 + 5*grown[i]}px">{box.label}</span>
                    {#if front === i}
                        <span class="text-xs text-slate-500 leading-snug" in:fade={{duration: 250, delay: 100}}>{box.description}</span>
                    {/if}
                </div>
            {/each}
        {/if}
    </div>
    <div class="flex items-center gap-3">
        <SelectSlider class="flex-1" min={0} max={types.length - 1} step={1} {disabled} bind:value={focusIndex} on:input={onSlide} aria-label="Focus"/>
        <SpecificityPill bind:specificity {disabled} onInput={onSpecificityInput}/>
    </div>
    <div class="flex flex-wrap items-baseline justify-center gap-x-2 text-sm">
        <span class="text-slate-600">{targetAthlete} · {Math.round(durations[targetAthlete]/60)} minute power</span>
        <span class="font-semibold tabular-nums whitespace-nowrap">
            {Math.round(currentFocusPower)}w → {Math.round(focusPower)}w
            <span class="text-xerteal-700">(+{Math.round(focusPower) - Math.round(currentFocusPower)}w)</span>
        </span>
    </div>
</div>
